import type { InvoicesByFiltersQueryVariables ,
  CreditTypeEnum} from "@/__generated__/graphql";
import {
  InvoicePaymentStatusEnum,
  InvoiceSortByEnum,
  InvoiceStatusEnum,
  SortEnum,
} from "@/__generated__/graphql";
import { InvoiceFilterStatusEnum } from "@/types/invoice";
import type { ClientCreditAccounts } from "@/types/profile";
import type { DepartmentCreditType } from "./payment-method/type";
import MassageCreditAccount from "@/assets/images/3d-icons/home-massage.png";
import BeautyCreditAccount from "@/assets/images/3d-icons/home-beauty.png";
import PetCreditAccount from "@/assets/images/3d-icons/pet-sitting.png";

export const getInvoiceFilters = (
  clientId: string,
  filterStatus: InvoiceFilterStatusEnum,
): InvoicesByFiltersQueryVariables["filters"] => {
  const filters = {
    clientId: [clientId],
    status: [InvoiceStatusEnum.Confirmed],
    sortBy: [InvoiceSortByEnum.IssueDate],
    sort: [SortEnum.Desc],
    limit: 10,
    offset: 0,
  };

  switch (filterStatus) {
    case InvoiceFilterStatusEnum.UNPAID:
      return {
        ...filters,
        paymentStatus: [
          InvoicePaymentStatusEnum.Unpaid,
          InvoicePaymentStatusEnum.Underpaid,
          InvoicePaymentStatusEnum.StripeProcessing,
        ],
      };
    case InvoiceFilterStatusEnum.PAID:
      return {
        ...filters,
        paymentStatus: [InvoicePaymentStatusEnum.Paid],
      };

    default:
      return filters;
  }
};

export const InvoiceFilterOptions: Record<InvoiceFilterStatusEnum, string> = {
  [InvoiceFilterStatusEnum.UNPAID]: "Unpaid",
  [InvoiceFilterStatusEnum.PAID]: "Paid",
};

export const invoiceTabs = [
  { label: "Unpaid", href: "/profile/invoices/unpaid" },
  { label: "Paid", href: "/profile/invoices/paid" },
];

export const getAccountCreditBalance = ({
  creditAccounts,
  type,
}: {
  creditAccounts: ClientCreditAccounts;
  type: CreditTypeEnum;
}) => {
  return (
    creditAccounts.find((credit) => credit.creditType === type)?.balance ?? 0
  );
};

export type DepartmentCreditPageKey =
  | "creditAccountMassage"
  | "creditAccountBeauty"
  | "creditAccountPet";

export type DepartmentCreditConfig = {
  label: string;
  linkedName: string;
  icon: string;
  pageKey: DepartmentCreditPageKey;
};

// Central mapping for department credit types.
// Used to keep UI labels, GraphQL `linkedObject.name`, and navigation `pageKey`
const DEPARTMENT_CREDIT_CONFIG: Record<
  DepartmentCreditType,
  DepartmentCreditConfig
> = {
  massage: {
    label: "Massage",
    linkedName: "MASSAGE",
    icon: MassageCreditAccount,
    pageKey: "creditAccountMassage",
  },
  beauty: {
    label: "Beauty",
    linkedName: "HOME_BEAUTY",
    icon: BeautyCreditAccount,
    pageKey: "creditAccountBeauty",
  },
  pet: {
    label: "Pet Care",
    linkedName: "PET_CARE",
    icon: PetCreditAccount,
    pageKey: "creditAccountPet",
  },
};

export const getDepartmentCreditConfig = (
  departmentCreditType: DepartmentCreditType | null,
): DepartmentCreditConfig | null => {
  if (!departmentCreditType) return null;
  return DEPARTMENT_CREDIT_CONFIG[departmentCreditType] ?? null;
};
